import { useTranslation } from 'react-i18next';
import { type LoadingFailure, type Progress, LoadingModalComponent } from '@/generic/progress';
import { Optional } from '@/generic/functional';
import { DiagnosticNotFollowed } from '@/diagnostic/domain/Diagnostic.ts';

export const DiagnosticLoadingModalComponent = ({
  open,
  progress,
  error,
  onRetry,
  onClose,
  onContinueInBackground,
}: {
  open: boolean;
  progress: Optional<Progress>;
  error: Optional<Error>;
  onRetry: () => void;
  onClose: () => void;
  onContinueInBackground: () => void;
}) => {
  const { t } = useTranslation('diagnostic');

  const notFollowedFailure = (value: Error): LoadingFailure => ({
    error: value,
    title: t('diagnostic.loading.error.follow.title'),
    description: t('diagnostic.loading.error.follow.description'),
    detailLabel: t('diagnostic.loading.error.detail'),
    retryLabel: t('diagnostic.loading.error.follow.retry'),
    closeLabel: t('diagnostic.loading.error.close'),
    onRetry,
  });

  const notStartedFailure = (value: Error): LoadingFailure => ({
    error: value,
    title: t('diagnostic.loading.error.start.title'),
    description: t('diagnostic.loading.error.start.description'),
    detailLabel: t('diagnostic.loading.error.detail'),
    retryLabel: t('diagnostic.loading.error.start.retry'),
    closeLabel: t('diagnostic.loading.error.close'),
    onRetry,
  });

  const failure: Optional<LoadingFailure> = error.map((value) =>
    value instanceof DiagnosticNotFollowed ? notFollowedFailure(value) : notStartedFailure(value),
  );

  return (
    <LoadingModalComponent
      open={open}
      title={t('diagnostic.loading.title')}
      description={t('diagnostic.loading.description')}
      progress={progress}
      failure={failure}
      cancelLabel={t('diagnostic.loading.cancel')}
      onClose={onClose}
      continueInBackgroundLabel={t('diagnostic.loading.continue')}
      onContinueInBackground={onContinueInBackground}
      dataSelector="diagnostic.loading-modal"
    />
  );
};
